import {
  Badge,
  Button,
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Separator,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';
import { Anchor, Calendar, Layers, Ruler, Ship, Users } from 'lucide-react';
import type React from 'react';
import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';

import { type EquipmentTier, type MarketBoat, marketDataApi } from '../lib/api-client';
import {
  bandLabel,
  bandToneClass,
  describeScore,
  groupEquipmentByTier,
} from '../lib/equipment-score';
import { queryKeys } from '../lib/query-keys';
import { AddToCompSetItems } from './add-to-comp-set-menu';
import { BoatTypeBadge } from './boat-type-badge';

interface MarketBoatDetailSheetProps {
  boat: MarketBoat | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MarketBoatDetailSheet({ boat, open, onOpenChange }: MarketBoatDetailSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        {boat ? <MarketBoatDetailContent boat={boat} /> : null}
      </SheetContent>
    </Sheet>
  );
}

function MarketBoatDetailContent({ boat }: { boat: MarketBoat }) {
  const { data: pricingData, isLoading: pricingLoading } = useQuery({
    queryKey: queryKeys.market.boat(boat.id, 'pricing'),
    queryFn: () => marketDataApi.pricing(boat.id, { limit: 100 }),
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: queryKeys.market.boat(boat.id, 'price-history'),
    queryFn: () => marketDataApi.priceHistory(boat.id, { limit: 200 }),
  });

  const pricing = pricingData?.data ?? [];
  const history = historyData?.data ?? [];

  return (
    <>
      <SheetHeader>
        <div className="flex items-center gap-2">
          <SheetTitle className="text-lg">{boat.name}</SheetTitle>
          <span
            className={`size-2.5 rounded-full ${boat.isActive ? 'bg-green-500' : 'bg-gray-400'}`}
          />
        </div>
        <SheetDescription asChild>
          <div className="flex items-center gap-2">
            {boat.model && <span>{boat.model}</span>}
            <BoatTypeBadge kind={boat.kind} />
          </div>
        </SheetDescription>
      </SheetHeader>

      <div className="space-y-6 px-4 pb-6">
        {/* Specifications */}
        <section>
          <h3 className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wide">
            Specifications
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <SpecItem icon={Calendar} label="Year" value={boat.year?.toString()} />
            <SpecItem
              icon={Ruler}
              label="Length"
              value={boat.lengthM ? `${boat.lengthM} m` : null}
            />
            <SpecItem icon={Ruler} label="Beam" value={boat.beamM ? `${boat.beamM} m` : null} />
            <SpecItem
              icon={Anchor}
              label="Draught"
              value={boat.draughtM ? `${boat.draughtM} m` : null}
            />
            <SpecItem icon={Ship} label="Cabins" value={boat.cabins?.toString()} />
            <SpecItem icon={Ship} label="Berths" value={boat.berths?.toString()} />
            <SpecItem icon={Ship} label="WC" value={boat.wc?.toString()} />
            <SpecItem icon={Users} label="Max People" value={boat.maxPeople?.toString()} />
          </div>
        </section>

        <Separator />

        {/* Details */}
        <section>
          <h3 className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wide">
            Details
          </h3>
          <dl className="space-y-2 text-sm">
            <DetailRow label="Charter Company" value={boat.charterName} />
            <DetailRow label="Home Base" value={boat.homeBase} />
            <DetailRow label="Shipyard" value={boat.shipyard} />
            <DetailRow label="Currency" value={boat.defaultCurrency} />
          </dl>
        </section>

        <Separator />

        {/* Equipment */}
        <EquipmentSection boat={boat} />

        <Separator />

        {/* Add to Competitor Set */}
        <section>
          <h3 className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wide">
            Competitor Sets
          </h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-full">
                <Layers className="mr-2 size-4" />
                Add to Competitor Set
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[200px]">
              <AddToCompSetItems marketBoatPids={[boat.id]} />
            </DropdownMenuContent>
          </DropdownMenu>
        </section>

        <Separator />

        {/* Current Pricing Chart */}
        <section>
          <h3 className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wide">
            Current Pricing
          </h3>
          {pricingLoading ? (
            <Skeleton className="h-[200px] w-full" />
          ) : pricing.length > 0 ? (
            <PricingBarChart periods={pricing} currency={boat.defaultCurrency ?? 'EUR'} />
          ) : (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No pricing data available.
            </p>
          )}
        </section>

        <Separator />

        {/* Price History Chart */}
        <section>
          <h3 className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wide">
            Price History
          </h3>
          {historyLoading ? (
            <Skeleton className="h-[200px] w-full" />
          ) : history.length > 0 ? (
            <PriceHistoryChart snapshots={history} currency={boat.defaultCurrency ?? 'EUR'} />
          ) : (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No price history available yet.
            </p>
          )}
        </section>

        <Separator />

        {/* Metadata */}
        <section className="text-muted-foreground text-xs">
          <p>Last seen: {new Date(boat.lastSeenAt).toLocaleDateString()}</p>
          <p>First tracked: {new Date(boat.createdAt).toLocaleDateString()}</p>
        </section>
      </div>
    </>
  );
}

function SpecItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border p-2">
      <Icon className="text-muted-foreground size-4 shrink-0" />
      <div>
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="text-sm font-medium">{value ?? '—'}</p>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value ?? '—'}</dd>
    </div>
  );
}

const pricingChartConfig = {
  price: { label: 'Price', color: 'var(--chart-1)' },
  listPrice: { label: 'List Price', color: 'var(--chart-2)' },
} satisfies ChartConfig;

function PricingBarChart({
  periods,
  currency,
}: {
  periods: {
    dateFrom: string;
    dateTo: string;
    price: string;
    listPrice: string | null;
    discountPct: string | null;
  }[];
  currency: string;
}) {
  const chartData = useMemo(
    () =>
      periods.map((p) => ({
        label: formatDateRange(p.dateFrom, p.dateTo),
        price: Number(p.price),
        listPrice: p.listPrice ? Number(p.listPrice) : undefined,
      })),
    [periods]
  );

  const formatter = useMemo(
    () => new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 0 }),
    [currency]
  );

  return (
    <ChartContainer config={pricingChartConfig} className="aspect-auto h-[200px] w-full">
      <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          angle={-45}
          textAnchor="end"
          height={60}
          fontSize={10}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => formatter.format(v)}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(value, name) => (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-muted-foreground">
                    {pricingChartConfig[name as keyof typeof pricingChartConfig]?.label ??
                      String(name)}
                  </span>
                  <span className="font-mono font-medium tabular-nums">
                    {formatter.format(value as number)}
                  </span>
                </div>
              )}
            />
          }
        />
        <Bar dataKey="price" fill="var(--color-price)" radius={[4, 4, 0, 0]} />
        <Bar
          dataKey="listPrice"
          fill="var(--color-listPrice)"
          radius={[4, 4, 0, 0]}
          opacity={0.5}
        />
      </BarChart>
    </ChartContainer>
  );
}

const historyChartConfig = {
  price: { label: 'Price', color: 'var(--chart-1)' },
} satisfies ChartConfig;

function PriceHistoryChart({
  snapshots,
  currency,
}: {
  snapshots: { fetchedAt: string; price: string }[];
  currency: string;
}) {
  const chartData = useMemo(
    () =>
      snapshots
        .map((s) => ({
          date: new Date(s.fetchedAt).toLocaleDateString('en', { month: 'short', day: 'numeric' }),
          price: Number(s.price),
        }))
        .reverse(),
    [snapshots]
  );

  const formatter = useMemo(
    () => new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 0 }),
    [currency]
  );

  return (
    <ChartContainer config={historyChartConfig} className="aspect-auto h-[200px] w-full">
      <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} fontSize={10} />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => formatter.format(v)}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(value) => (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-muted-foreground">Price</span>
                  <span className="font-mono font-medium tabular-nums">
                    {formatter.format(value as number)}
                  </span>
                </div>
              )}
            />
          }
        />
        <Line
          type="monotone"
          dataKey="price"
          stroke="var(--color-price)"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ChartContainer>
  );
}

function formatDateRange(from: string, to: string): string {
  const f = new Date(from);
  const t = new Date(to);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${f.toLocaleDateString('en', opts)} – ${t.toLocaleDateString('en', opts)}`;
}

const TIER_HEADINGS: Record<EquipmentTier | 'unrated', string> = {
  premium: 'Premium',
  comfort: 'Comfort',
  baseline: 'Standard',
  unrated: 'Not rated',
};

/**
 * What the boat carries and what that adds up to. The index itself is only
 * meaningful against boats of the same hull type, so the score is always shown
 * with the sentence that explains what it is measured against.
 */
function EquipmentSection({ boat }: { boat: MarketBoat }) {
  const score = boat.equipmentScore;
  const groups = groupEquipmentByTier(boat.equipment);

  return (
    <section>
      <h3 className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wide">
        Equipment
      </h3>

      {score ? (
        <div className="mb-3 flex items-baseline gap-2">
          <span className="text-2xl font-semibold tabular-nums">{score.score}</span>
          <Badge className={bandToneClass(score.band)}>{bandLabel(score.band)}</Badge>
        </div>
      ) : (
        <p className="mb-3 text-sm text-muted-foreground">
          {boat.equipment.length === 0
            ? 'No equipment data for this boat.'
            : 'Not rated — the index only covers sailing and motor boats.'}
        </p>
      )}

      {score && <p className="mb-4 text-xs text-muted-foreground">{describeScore(score)}</p>}

      {groups.length > 0 && (
        <div className="space-y-3">
          {groups.map((group) => (
            <div key={group.tier ?? 'unrated'}>
              <p className="mb-1 text-xs font-medium">{TIER_HEADINGS[group.tier ?? 'unrated']}</p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.id} className="flex justify-between gap-3 text-sm">
                    <span>{item.name}</span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {item.points === null
                        ? '—'
                        : `${item.points} pt${item.points === 1 ? '' : 's'}`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
